import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  DocumentData,
  getDocs,
  limit,
  orderBy,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  startAfter,
  Timestamp,
  where,
} from 'firebase/firestore';
import { Download, History, RefreshCw, Search } from 'lucide-react';
import { WorkshopType, WorkshopV2 } from '../../interfaces/workshop.interface';
import { Participant } from '../../interfaces/reservation.interface';
import { db } from '../../firebase';
import {
  fetchParticipantsByWorkshop,
  participantRevenue,
  reservationPlaces,
} from '../../utils/reservation';
import { getTypeLabel } from '../../utils/workshopMigration';
import SkeletonWorkshopCard from '../workshops/components/SkeletonWorkshopCard';
import HistoryStats, {
  HistorySummary,
  ParticipantsState,
} from './components/HistoryStats';
import PastWorkshopCard from './components/PastWorkshopCard';
import WorkshopHistoryPopup from './components/WorkshopHistoryPopup';
import { exportHistory } from './utils/exportParticipants';

/** Ateliers chargés par lot ; « Charger plus » en ajoute autant. */
const PAGE_SIZE = 20;

/** Garde-fou si un atelier porte une date aberrante (import, saisie). */
const MAX_YEAR_OPTIONS = 30;

const TYPE_OPTIONS: WorkshopType[] = ['public', 'health', 'teambuilding'];

type YearFilter = 'all' | number;
type Cursor = QueryDocumentSnapshot<DocumentData> | null;

/**
 * Borne temporelle de la requête. Deux inégalités sur `date` restent permises
 * tant qu'elles portent sur le champ trié — pas d'index composite nécessaire.
 */
const pastConstraints = (year: YearFilter): QueryConstraint[] => {
  const now = new Date();
  if (year === 'all') return [where('date', '<', Timestamp.fromDate(now))];

  const start = new Date(year, 0, 1);
  const nextYear = new Date(year + 1, 0, 1);
  // L'année en cours s'arrête à aujourd'hui : l'historique ne montre pas le futur.
  const end = nextYear.getTime() < now.getTime() ? nextYear : now;
  return [
    where('date', '>=', Timestamp.fromDate(start)),
    where('date', '<', Timestamp.fromDate(end)),
  ];
};

const fetchPage = async (year: YearFilter, cursor: Cursor) => {
  // On demande un élément de plus que la page pour savoir s'il reste du stock.
  const constraints: QueryConstraint[] = [
    ...pastConstraints(year),
    orderBy('date', 'desc'),
    ...(cursor ? [startAfter(cursor)] : []),
    limit(PAGE_SIZE + 1),
  ];

  const snapshot = await getDocs(
    query(collection(db, 'workshops_v2'), ...constraints)
  );
  const docs = snapshot.docs.slice(0, PAGE_SIZE);
  const items = docs
    .map((doc) => ({ ...doc.data(), id: doc.id }) as WorkshopV2)
    // Un document sans vraie Timestamp ferait planter tout le rendu sur .toDate()
    .filter((workshop) => typeof workshop.date?.toDate === 'function');

  return {
    items,
    cursor: docs.length > 0 ? docs[docs.length - 1] : null,
    hasMore: snapshot.docs.length > PAGE_SIZE,
  };
};

const WorkshopHistoryPage = () => {
  const [workshops, setWorkshops] = useState<WorkshopV2[]>([]);
  const [participants, setParticipants] = useState<
    Record<string, Participant[]>
  >({});
  const [cursor, setCursor] = useState<Cursor>(null);
  const [hasMore, setHasMore] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Distinct de `error` : un échec de pagination ne doit pas escamoter les
   * ateliers déjà affichés, il s'annonce à côté du bouton qui l'a provoqué.
   */
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [participantsError, setParticipantsError] = useState<string | null>(
    null
  );

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<WorkshopType | 'all'>('all');
  const [year, setYear] = useState<YearFilter>('all');
  const [firstYear, setFirstYear] = useState<number | null>(null);
  const [selected, setSelected] = useState<WorkshopV2 | null>(null);

  /** Invalide les réponses des chargements abandonnés (changement de filtre). */
  const requestId = useRef(0);
  /** Même principe, propre au bouton « charger plus », pour ne pas le figer. */
  const loadMoreId = useRef(0);
  /** Empêche deux « charger plus » simultanés si les clics sont groupés. */
  const loadMoreBusy = useRef(false);
  /**
   * Le lot de participants de la première page et celui d'un « charger plus »
   * peuvent être en vol en même temps. On suit les requêtes ouvertes de la
   * génération courante : tant qu'il en reste une, l'écran reste en chargement,
   * sinon la plus rapide annoncerait « prêt » alors que l'autre n'a pas répondu.
   *
   * Le compteur est porté par la génération : une requête abandonnée par un
   * changement d'année ne doit ni la maintenir en chargement, ni la libérer.
   */
  const pendingParticipants = useRef({
    generation: 0,
    count: 0,
    failed: false,
  });

  const loadParticipants = useCallback(
    async (items: WorkshopV2[], id: number) => {
      if (items.length === 0) return;

      const pending = pendingParticipants.current;
      if (pending.generation !== id) return; // génération abandonnée
      pending.count += 1;
      setParticipantsLoading(true);

      try {
        const map = await fetchParticipantsByWorkshop(items.map((w) => w.id));
        if (id !== requestId.current) return;
        // Toujours fusionner : c'est `load` qui remet la table à zéro, sinon un
        // lot arrivé dans le désordre effacerait celui de la page précédente.
        setParticipants((prev) => ({ ...prev, ...map }));
      } catch (err) {
        console.error('Erreur chargement des réservations :', err);
        if (id !== requestId.current) return;
        // Marqué sur la génération : un lot qui réussit ensuite ne doit pas
        // effacer l'avertissement, les participants du lot raté manquent tou-
        // jours à l'appel.
        pendingParticipants.current.failed = true;
        setParticipantsError(
          String(err).includes('Missing or insufficient permissions')
            ? 'Accès refusé aux réservations. Vérifiez vos règles Firestore / rôle admin.'
            : 'Les participants n’ont pas pu être chargés.'
        );
      } finally {
        const current = pendingParticipants.current;
        if (current.generation === id) {
          current.count -= 1;
          if (current.count === 0) {
            setParticipantsLoading(false);
            if (!current.failed) setParticipantsError(null);
          }
        }
      }
    },
    []
  );

  const load = useCallback(
    async (target: YearFilter) => {
      const id = ++requestId.current;
      loadMoreId.current += 1; // un « charger plus » en vol devient caduc
      // Nouvelle génération : les lots de participants en vol sont abandonnés.
      pendingParticipants.current = { generation: id, count: 0, failed: false };
      setLoading(true);
      setLoadingMore(false);
      setError(null);
      setLoadMoreError(null);
      setParticipantsError(null);
      // Une requête abandonnée ne rendra jamais la main : on repart de zéro.
      setParticipantsLoading(false);

      try {
        const page = await fetchPage(target, null);
        if (id !== requestId.current) return;

        setWorkshops(page.items);
        setParticipants({});
        setCursor(page.cursor);
        setHasMore(page.hasMore);
        setLoading(false);

        // Les cartes s'affichent immédiatement, les participants complètent après.
        await loadParticipants(page.items, id);
      } catch (err) {
        console.error("Erreur lors du chargement de l'historique :", err);
        if (id !== requestId.current) return;
        setError("Impossible de charger l'historique des ateliers.");
        setLoading(false);
        setParticipantsLoading(false);
        // Sans ça, les tuiles afficheraient les totaux de la requête précédente
        // juste au-dessus du message d'erreur.
        setWorkshops([]);
        setParticipants({});
        setCursor(null);
        setHasMore(false);
      }
    },
    [loadParticipants]
  );

  useEffect(() => {
    load(year);
  }, [load, year]);

  // Année du plus ancien atelier : alimente la liste déroulante des années.
  useEffect(() => {
    const run = async () => {
      try {
        const snapshot = await getDocs(
          query(
            collection(db, 'workshops_v2'),
            where('date', '<', Timestamp.fromDate(new Date())),
            orderBy('date', 'asc'),
            limit(1)
          )
        );
        const oldest = snapshot.docs[0]?.data() as DocumentData | undefined;
        if (oldest?.date?.toDate) {
          setFirstYear(oldest.date.toDate().getFullYear());
        }
      } catch (err) {
        console.error('Erreur lors du calcul de la première année :', err);
      }
    };
    run();
  }, []);

  const handleLoadMore = async () => {
    // Le ref, et pas seulement l'état : deux clics groupés dans le même lot de
    // rendu liraient tous les deux `loadingMore === false`.
    if (!cursor || loading || loadingMore || loadMoreBusy.current) return;
    loadMoreBusy.current = true;
    const id = requestId.current;
    const token = ++loadMoreId.current;
    setLoadingMore(true);
    setLoadMoreError(null);

    try {
      const page = await fetchPage(year, cursor);
      if (id !== requestId.current) return;

      setWorkshops((prev) => [...prev, ...page.items]);
      setCursor(page.cursor);
      setHasMore(page.hasMore);
      await loadParticipants(page.items, id);
    } catch (err) {
      console.error('Erreur lors du chargement des ateliers suivants :', err);
      if (id === requestId.current) {
        setLoadMoreError(
          "Impossible de charger la suite de l'historique. Réessayez."
        );
      }
    } finally {
      loadMoreBusy.current = false;
      // Seul le dernier « charger plus » a le droit de rendre la main au bouton.
      if (token === loadMoreId.current) setLoadingMore(false);
    }
  };

  const yearOptions = useMemo(() => {
    if (firstYear === null) return [];
    const current = new Date().getFullYear();
    const years: number[] = [];
    for (
      let y = current;
      y >= firstYear && years.length < MAX_YEAR_OPTIONS;
      y--
    ) {
      years.push(y);
    }
    return years;
  }, [firstYear]);

  // Recherche et type filtrent côté client, sur ce qui est déjà chargé ;
  // l'année, elle, repart en base pour couvrir tout l'historique.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return workshops.filter((workshop) => {
      if (typeFilter !== 'all' && workshop.type !== typeFilter) return false;
      if (!term) return true;

      if ((workshop.title || '').toLowerCase().includes(term)) return true;
      if ((workshop.location || '').toLowerCase().includes(term)) return true;

      return (participants[workshop.id] || []).some(
        (p) =>
          p.displayName.toLowerCase().includes(term) ||
          p.email.toLowerCase().includes(term)
      );
    });
  }, [workshops, participants, search, typeFilter]);

  const summary = useMemo<HistorySummary>(() => {
    let bookingCount = 0;
    let placesCount = 0;
    let capacity = 0;
    let revenue = 0;

    filtered.forEach((workshop) => {
      const rows = participants[workshop.id] || [];
      bookingCount += rows.length;
      capacity += workshop.maxParticipants || 0;
      rows.forEach((p) => {
        placesCount += reservationPlaces(p);
        revenue += participantRevenue(p, workshop.priceSolo, workshop.priceDuo);
      });
    });

    return {
      workshopCount: filtered.length,
      bookingCount,
      placesCount,
      capacity,
      fillRate: capacity > 0 ? Math.round((placesCount / capacity) * 100) : 0,
      revenue,
    };
  }, [filtered, participants]);

  const isFiltering = search.trim().length > 0 || typeFilter !== 'all';

  const participantsState: ParticipantsState = participantsError
    ? 'unavailable'
    : participantsLoading
      ? 'loading'
      : 'ready';

  /**
   * Exporter sans les réservations produirait un fichier « 0 personne partout »
   * impossible à distinguer d'un historique réellement vide. On vérifie la
   * donnée elle-même plutôt qu'un drapeau : `fetchParticipantsByWorkshop`
   * pré-remplit chaque id demandé, donc une entrée absente signifie bien
   * « jamais chargé » et non « aucune réservation ».
   */
  const participantsComplete = filtered.every(
    (workshop) => participants[workshop.id] !== undefined
  );
  const canExport = !loading && filtered.length > 0 && participantsComplete;

  const exportTitle = !canExport
    ? filtered.length === 0
      ? 'Aucun atelier à exporter'
      : 'Export indisponible tant que les réservations ne sont pas chargées'
    : 'Exporter la sélection au format CSV';

  /**
   * Message d'absence de résultat. On distingue « il n'y a rien » de « rien
   * ici, mais il reste des ateliers à charger » : les filtres texte et type ne
   * portent que sur les ateliers déjà chargés, contrairement à l'année.
   */
  const emptyMessage = () => {
    if (participantsState === 'loading' && search.trim()) {
      return 'Recherche en cours, les participants finissent de charger…';
    }
    if (workshops.length === 0) {
      if (hasMore) return 'Aucun atelier affichable ici. Chargez-en davantage.';
      if (year !== 'all') return `Aucun atelier passé en ${year}.`;
      return 'Aucun atelier passé pour le moment.';
    }
    if (hasMore) {
      return `Aucun résultat parmi les ${workshops.length} ateliers chargés. Chargez-en davantage pour élargir la recherche.`;
    }
    return 'Aucun atelier ne correspond à ces filtres.';
  };

  return (
    <div className="h-full flex flex-col p-6 gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold text-[#623413]">
            <History size={22} />
            Historique des ateliers
          </h2>
          <p className="text-sm text-gray-500">
            Consultez les ateliers passés, leurs participants et leur
            remplissage.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => load(year)}
            className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100"
            title="Rafraîchir"
          >
            <RefreshCw size={16} />
          </button>
          <button
            type="button"
            onClick={() => exportHistory(filtered, participants)}
            disabled={!canExport}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
            title={exportTitle}
          >
            <Download size={16} />
            Exporter
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Atelier, lieu ou participant…"
            className="w-72 rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#623413]/40"
          />
        </div>

        <select
          value={typeFilter}
          onChange={(e) =>
            setTypeFilter(e.target.value as WorkshopType | 'all')
          }
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#623413]/40"
        >
          <option value="all">Tous les types</option>
          {TYPE_OPTIONS.map((type) => (
            <option key={type} value={type}>
              {getTypeLabel(type)}
            </option>
          ))}
        </select>

        <select
          value={String(year)}
          onChange={(e) =>
            setYear(e.target.value === 'all' ? 'all' : Number(e.target.value))
          }
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#623413]/40"
        >
          <option value="all">Toutes les années</option>
          {yearOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <HistoryStats
        summary={summary}
        loading={loading}
        participantsState={participantsState}
      />

      {participantsError && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
          {participantsError}
        </p>
      )}

      <div className="flex-1 overflow-y-auto flex flex-col gap-4 pr-2">
        {loading ? (
          [...Array(4)].map((_, i) => <SkeletonWorkshopCard key={i} />)
        ) : error ? (
          <p className="py-10 text-center text-sm text-[#BB0F33]">{error}</p>
        ) : (
          <>
            {filtered.length === 0 && (
              <p className="py-10 text-center text-sm text-gray-500">
                {emptyMessage()}
              </p>
            )}

            {filtered.map((workshop) => (
              <PastWorkshopCard
                key={workshop.id}
                workshop={workshop}
                participants={participants[workshop.id] || []}
                participantsLoading={
                  participantsLoading && !participants[workshop.id]
                }
                participantsUnavailable={
                  !!participantsError && !participants[workshop.id]
                }
                onOpen={() => setSelected(workshop)}
              />
            ))}

            {/* Rendu même sans résultat : sinon un filtre qui ne matche rien
                supprimerait le seul moyen d'aller chercher les plus anciens. */}
            {(filtered.length > 0 || hasMore || loadMoreError) && (
              <div className="flex flex-col items-center gap-2 pb-2">
                {filtered.length > 0 && (
                  <p className="text-xs text-gray-400">
                    {filtered.length} atelier{filtered.length > 1 ? 's' : ''}{' '}
                    affiché{filtered.length > 1 ? 's' : ''}
                    {isFiltering &&
                      ` sur ${workshops.length} chargé${
                        workshops.length > 1 ? 's' : ''
                      }`}
                  </p>
                )}

                {loadMoreError && (
                  <p className="text-xs text-[#BB0F33]">{loadMoreError}</p>
                )}

                {hasMore && (
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
                  >
                    {loadingMore
                      ? 'Chargement…'
                      : loadMoreError
                        ? 'Réessayer'
                        : 'Charger plus d’ateliers'}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {selected && (
        <WorkshopHistoryPopup
          workshop={selected}
          participants={participants[selected.id]}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

export default WorkshopHistoryPage;
